import { useTranslation } from 'react-i18next';
import { t } from 'i18next';
import { ProgrammingDialog } from './ProgrammingDialog';
// Adapted from Rebranding-Soverdi b4b51ac7: original five-step UI, B4 draft-only adapter.
import React, { useState, useEffect, useRef } from 'react';
import {
  X, ChevronRight, ChevronLeft, Check, ArrowUp, ArrowDown, Trash2,
  Droplets, FlaskConical, Leaf, Square, Clock, Calendar, Settings2,
  Play, AlertTriangle
} from 'lucide-react';
import { useCgmDisplayMode } from '../contexts/CgmDisplayModeContext';
import {
  ProgrammableSector, ProgramStep, ProgramSchedule, ProgramType, RecurrenceType,
  draftPayload, saveDraft, computeProgramDuration, computeTimeline,
  formatDuration, programmingApiError, RECOMMENDED_OVERLAP_SECONDS
} from './programmingService';

interface ProgramWizardProps {
  sectors: ProgrammableSector[];
  farmId: string;
  onClose: () => void;
  onSaved: () => void;
}

const weekDays = () => [
  { value: 1, label: t("programming:text15") }, { value: 2, label: t("programming:text16") }, { value: 3, label: t("programming:text17") },
  { value: 4, label: t("programming:text18") }, { value: 5, label: t("programming:text19") }, { value: 6, label: t("programming:text20") }, { value: 0, label: t("programming:text21") },
];

const ProgramWizard: React.FC<ProgramWizardProps> = ({ onClose, onSaved, sectors, farmId }) => {
  useTranslation('programming');
  const { isExpert } = useCgmDisplayMode();
  const [error, setError] = useState('');
  const [timezone, setTimezone] = useState('Europe/Paris');
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const creationRequestId = useRef<string | undefined>(undefined);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Form state
  const [name, setName] = useState('');
  const [type, setType] = useState<ProgramType>('irrigation');
  const [selectedSectorIds, setSelectedSectorIds] = useState<string[]>([]);
  const [steps, setSteps] = useState<ProgramStep[]>([]);
  const [globalOverlap, setGlobalOverlap] = useState<number>(RECOMMENDED_OVERLAP_SECONDS);
  const [overlapMode, setOverlapMode] = useState<'none' | 'recommended' | 'custom'>('recommended');
  const [schedule, setSchedule] = useState<ProgramSchedule>({
    startMode: 'scheduled',
    startTime: '06:00',
    recurrence: 'daily',
    weekDays: [1, 3, 5],
    cycleIntervalDays: 3,
  });

  const totalSteps = 5;


  // Build steps when sectors are selected
  useEffect(() => {
    setSteps(prev => {
      // Keep existing steps that are still selected, add new ones
      const existing = prev.filter(s => selectedSectorIds.includes(s.sectorId));
      const existingSectorIds = existing.map(s => s.sectorId);
      const newOnes = selectedSectorIds
        .filter(id => !existingSectorIds.includes(id))
        .map((id) => {
          const sector = sectors.find(s => s.id === id);
          return {
            id: `step-${id}`,
            order: 0,
            sectorId: id,
            sectorName: sector?.name || id,
            culture: sector?.culture,
            valveIds: sector?.valveIds || [],
            duration_minutes: 45,
            overlap_seconds: overlapMode === 'none' ? 0 : globalOverlap,
          };
        });
      const combined = [...existing, ...newOnes];
      return combined.map((s, i) => ({ ...s, order: i + 1, overlap_seconds: overlapMode === 'custom' ? s.overlap_seconds : overlapMode === 'none' ? 0 : RECOMMENDED_OVERLAP_SECONDS }));
    });
  }, [selectedSectorIds, sectors, overlapMode, globalOverlap]);

  // Apply overlap mode to all steps
  useEffect(() => {
    const overlapValue = overlapMode === 'none' ? 0 : globalOverlap;
    setSteps(prev => prev.map((s, i) => ({
      ...s,
      overlap_seconds: overlapValue, // final transition ignored by canonical payload
    })));
  }, [overlapMode, globalOverlap]);

  const toggleSector = (id: string) => {
    setSelectedSectorIds(prev =>
      prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]
    );
  };

  const moveStep = (index: number, direction: 'up' | 'down') => {
    setSteps(prev => {
      const newSteps = [...prev];
      const targetIndex = direction === 'up' ? index - 1 : index + 1;
      if (targetIndex < 0 || targetIndex >= newSteps.length) return prev;
      [newSteps[index], newSteps[targetIndex]] = [newSteps[targetIndex], newSteps[index]];
      return newSteps.map((s, i) => ({ ...s, order: i + 1 }));
    });
  };

  const removeStep = (id: string) => {
    setSelectedSectorIds(prev => prev.filter(sid => {
      const step = steps.find(s => s.id === id);
      return step ? sid !== step.sectorId : true;
    }));
  };

  const updateStepDuration = (id: string, duration: number) => {
    setSteps(prev => prev.map(s => s.id === id ? { ...s, duration_minutes: duration } : s));
  };

  const updateStepOverlap = (id: string, overlap: number) => {
    setSteps(prev => prev.map(s => s.id === id ? { ...s, overlap_seconds: Math.max(0, overlap) } : s));
  };

  const handleSave = async () => {
    if (savingRef.current) return;
    savingRef.current = true;
    setError('');
    setSaving(true);
    try {
      creationRequestId.current ||= crypto.randomUUID();
      await saveDraft(draftPayload(farmId, timezone, name, type, steps, schedule, sectors), creationRequestId.current);
      onSaved();
    } catch (error) {
      setError(programmingApiError(error));
    } finally {
      setSaving(false);
      savingRef.current = false;
    }
  };

  let validationError = '';
  if (step === 5) {
    try { draftPayload(farmId, timezone, name, type, steps, schedule, sectors); }
    catch (e) { validationError = e instanceof Error ? e.message : t('programming:invalidData'); }
  }
  const canProceed = () => {
    if (step === 1) return name.trim().length > 0;
    if (step === 2) return selectedSectorIds.length > 0;
    if (step === 3) return steps.length > 0;
    if (step === 5) return !validationError;
    return true;
  };

  const totalDuration = computeProgramDuration(steps);
  const timeline = computeTimeline(steps, schedule.startTime || '06:00');

  return (
    <ProgrammingDialog onClose={onClose} saving={saving} label={t("programming:text6")}>
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" data-testid="program-wizard">
      <div className="fixed inset-0 bg-black/40" onClick={() => { if (!savingRef.current) onClose(); }} />

      <div className="relative bg-[var(--surface)] w-full sm:max-w-2xl sm:rounded-2xl rounded-t-2xl shadow-2xl max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="shrink-0 px-4 sm:px-6 py-4 border-b border-[var(--border-light)] flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-[var(--text-primary)]">
              {t("programming:text6")}</h2>
            <p className="text-xs text-[var(--text-tertiary)]">{t("programming:text23")} {step} / {totalSteps}</p>
          </div>
          <button disabled={saving} aria-label={t("programming:close")} onClick={onClose} className="p-2 rounded-lg hover:bg-[var(--gray-100)] min-w-[44px] min-h-[44px] flex items-center justify-center" data-testid="wizard-close">
            <X className="w-5 h-5 text-[var(--text-tertiary)]" />
          </button>
        </div>

        {/* Progress bar */}
        <div className="shrink-0 h-1 bg-[var(--gray-100)]">
          <div className="h-full bg-[var(--color-primary)] transition-all duration-300" style={{ width: `${(step / totalSteps) * 100}%` }} />
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-5">
          {/* STEP 1: General info */}
          {step === 1 && (
            <div className="space-y-5" data-testid="wizard-step-1">
              <div>
                <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">{t("programming:text24")}</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t("programming:text25")}
                  className="w-full px-4 py-3 rounded-lg border border-[var(--border-light)] bg-[var(--surface)] text-[var(--text-primary)] focus:border-[var(--color-primary)] focus:outline-none min-h-[48px]"
                  data-testid="program-name-input"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">{t("programming:text26")}</label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setType('irrigation')}
                    className={`flex items-center gap-3 p-4 rounded-lg border-2 transition-colors min-h-[64px] ${type === 'irrigation' ? 'border-[var(--color-primary)] bg-[var(--color-primary)]/5' : 'border-[var(--border-light)]'}`}
                    data-testid="type-irrigation"
                  >
                    <Droplets className={`w-6 h-6 ${type === 'irrigation' ? 'text-[var(--color-primary)]' : 'text-[var(--text-tertiary)]'}`} />
                    <span className="font-medium text-[var(--text-primary)]">{t("programming:text27")}</span>
                  </button>
                  <button
                    disabled title={t("programming:text28")}
                    className={`flex items-center gap-3 p-4 rounded-lg border-2 transition-colors min-h-[64px] ${type === 'fertigation' ? 'border-[var(--color-primary)] bg-[var(--color-primary)]/5' : 'border-[var(--border-light)]'}`}
                    data-testid="type-fertigation"
                  >
                    <FlaskConical className={`w-6 h-6 ${type === 'fertigation' ? 'text-[var(--color-primary)]' : 'text-[var(--text-tertiary)]'}`} />
                    <span className="font-medium text-[var(--text-primary)]">{t("programming:text29")}</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* STEP 2: Select sectors */}
          {step === 2 && (
            <div className="space-y-3" data-testid="wizard-step-2">
              <p className="text-sm text-[var(--text-secondary)]">{t("programming:text30")}</p>
              {sectors.map(sector => {
                const selected = selectedSectorIds.includes(sector.id);
                return (
                  <button
                    key={sector.id}
                    onClick={() => toggleSector(sector.id)}
                    disabled={!sector.equipmentOk}
                    className={`w-full flex items-center gap-3 p-4 rounded-lg border-2 transition-colors min-h-[64px] text-left ${
                      !sector.equipmentOk ? 'opacity-50 cursor-not-allowed border-[var(--border-light)]' :
                      selected ? 'border-[var(--color-primary)] bg-[var(--color-primary)]/5' : 'border-[var(--border-light)] hover:border-[var(--color-primary)]/40'
                    }`}
                    data-testid={`select-sector-${sector.id}`}
                  >
                    <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center shrink-0 ${selected ? 'bg-[var(--color-primary)] border-[var(--color-primary)]' : 'border-[var(--gray-300)]'}`}>
                      {selected && <Check className="w-4 h-4 text-white" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-[var(--text-primary)]">{sector.name}</p>
                      <div className="flex items-center gap-3 mt-0.5 text-xs text-[var(--text-tertiary)]">
                        {sector.culture && <span className="flex items-center gap-1"><Leaf className="w-3 h-3" />{sector.culture}</span>}
                        {sector.surface_ha && <span className="flex items-center gap-1"><Square className="w-3 h-3" />{sector.surface_ha} {t("programming:text31")}</span>}
                      </div>
                    </div>
                    {!sector.equipmentOk && (
                      <span className="flex items-center gap-1 text-xs text-[var(--color-danger)]"><AlertTriangle className="w-3.5 h-3.5" />{t("programming:text32")}</span>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {/* STEP 3: Build sequence */}
          {step === 3 && (
            <div className="space-y-3" data-testid="wizard-step-3">
              <p className="text-sm text-[var(--text-secondary)]">{t("programming:text33")}</p>
              {steps.map((s, index) => (
                <div key={s.id} className="flex items-center gap-2 p-3 rounded-lg border border-[var(--border-light)] bg-[var(--gray-50)]" data-testid={`sequence-step-${s.id}`}>
                  <div className="flex flex-col gap-1">
                    <button onClick={() => moveStep(index, 'up')} disabled={index === 0} className="p-1.5 rounded hover:bg-[var(--gray-200)] disabled:opacity-30 min-w-[32px] min-h-[32px] flex items-center justify-center" data-testid={`move-up-${s.id}`}>
                      <ArrowUp className="w-4 h-4" />
                    </button>
                    <button onClick={() => moveStep(index, 'down')} disabled={index === steps.length - 1} className="p-1.5 rounded hover:bg-[var(--gray-200)] disabled:opacity-30 min-w-[32px] min-h-[32px] flex items-center justify-center" data-testid={`move-down-${s.id}`}>
                      <ArrowDown className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="w-7 h-7 rounded-full bg-[var(--color-primary)] text-white flex items-center justify-center text-sm font-semibold shrink-0">
                    {index + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-[var(--text-primary)] truncate">{s.sectorName}</p>
                    <p className="text-xs text-[var(--text-tertiary)]">{t("programming:text34")}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <input
                      type="number"
                      value={s.duration_minutes}
                      onChange={(e) => updateStepDuration(s.id, Number(e.target.value))}
                      min={1 / 60} step="any"
                      className="w-16 px-2 py-2 rounded-lg border border-[var(--border-light)] text-center text-sm min-h-[44px]"
                      data-testid={`duration-${s.id}`}
                    />
                    <span className="text-xs text-[var(--text-tertiary)]">{t("programming:text35")}</span>
                  </div>
                  <button onClick={() => removeStep(s.id)} className="p-2 rounded-lg hover:bg-[var(--color-danger)]/10 text-[var(--color-danger)] min-w-[40px] min-h-[40px] flex items-center justify-center" data-testid={`remove-step-${s.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* STEP 4: Overlap */}
          {step === 4 && (
            <div className="space-y-5" data-testid="wizard-step-4">
              <div>
                <h3 className="text-sm font-medium text-[var(--text-primary)] mb-1">{t("programming:text36")}</h3>
                <p className="text-xs text-[var(--text-tertiary)] mb-3">{t("programming:text37")}</p>
                <div className="space-y-2">
                  {[
                    { value: 'none', label: t("programming:text38"), desc: t('programming:seconds', { count: 0 }) },
                    { value: 'recommended', label: t("programming:text39"), desc: t('programming:seconds', { count: 30 }) },
                    { value: 'custom', label: t("programming:text40"), desc: '' },
                  ].map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => { setOverlapMode(opt.value as 'none' | 'recommended' | 'custom'); if (opt.value === 'recommended') setGlobalOverlap(RECOMMENDED_OVERLAP_SECONDS); }}
                      className={`w-full flex items-center gap-3 p-3 rounded-lg border-2 transition-colors min-h-[56px] text-left ${overlapMode === opt.value ? 'border-[var(--color-primary)] bg-[var(--color-primary)]/5' : 'border-[var(--border-light)]'}`}
                      data-testid={`overlap-${opt.value}`}
                    >
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${overlapMode === opt.value ? 'border-[var(--color-primary)]' : 'border-[var(--gray-300)]'}`}>
                        {overlapMode === opt.value && <div className="w-2.5 h-2.5 rounded-full bg-[var(--color-primary)]" />}
                      </div>
                      <span className="flex-1 font-medium text-[var(--text-primary)]">{opt.label}</span>
                      {opt.desc && <span className="text-sm text-[var(--text-tertiary)]">{opt.desc}</span>}
                    </button>
                  ))}
                </div>
                {overlapMode === 'custom' && (
                  <div className="mt-3 flex items-center gap-2 p-3 rounded-lg bg-[var(--gray-50)]">
                    <span className="text-sm text-[var(--text-secondary)]">{t("programming:text41")}</span>
                    <input type="number" value={globalOverlap} onChange={(e) => setGlobalOverlap(parseInt(e.target.value) || 0)} className="w-20 px-2 py-2 rounded-lg border border-[var(--border-light)] text-center min-h-[44px]" data-testid="custom-overlap-input" />
                    <span className="text-sm text-[var(--text-tertiary)]">{t("programming:text42")}</span>
                  </div>
                )}
              </div>

              {/* Expert: per-transition overlap */}
              {isExpert && (
                <div>
                  <button onClick={() => setShowAdvanced(!showAdvanced)} className="flex items-center gap-2 text-sm font-medium text-[var(--color-primary)]" data-testid="toggle-advanced">
                    <Settings2 className="w-4 h-4" />
                    {t("programming:text43")}<ChevronRight className={`w-4 h-4 transition-transform ${showAdvanced ? 'rotate-90' : ''}`} />
                  </button>
                  {showAdvanced && (
                    <div className="mt-3 space-y-2" data-testid="advanced-options">
                      <p className="text-xs text-[var(--text-tertiary)]">{t("programming:text44")}</p>
                      {steps.slice(0, -1).map((s, i) => (
                        <div key={s.id} className="flex items-center gap-2 p-2 rounded-lg bg-[var(--gray-50)] text-sm">
                          <span className="flex-1 text-[var(--text-secondary)] truncate">{s.sectorName} → {steps[i + 1]?.sectorName}</span>
                          <input type="number" value={s.overlap_seconds} onChange={(e) => updateStepOverlap(s.id, parseInt(e.target.value) || 0)} className="w-16 px-2 py-1.5 rounded border border-[var(--border-light)] text-center min-h-[40px]" data-testid={`transition-overlap-${s.id}`} />
                          <span className="text-xs text-[var(--text-tertiary)]">{t("programming:text42")}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* STEP 5: Schedule + Preview */}
          {step === 5 && (
            <div className="space-y-5" data-testid="wizard-step-5">
              <p className="text-sm">{t("programming:text45")}</p>
              <label>{t("programming:text46")}<input aria-label={t("programming:text46")} value={timezone} onChange={e => setTimezone(e.target.value)} /></label>
              {/* Scheduling */}
              <div>
                <h3 className="text-sm font-medium text-[var(--text-primary)] mb-2">{t("programming:text47")}</h3>
                <div className="grid grid-cols-2 gap-2 mb-3">
                  <button disabled title={t("programming:text48")} className={`flex items-center justify-center gap-2 p-3 rounded-lg border-2 min-h-[48px] ${schedule.startMode === 'now' ? 'border-[var(--color-primary)] bg-[var(--color-primary)]/5' : 'border-[var(--border-light)]'}`} data-testid="start-now">
                    <Play className="w-4 h-4" /> {t("programming:text49")}</button>
                  <button onClick={() => setSchedule({ ...schedule, startMode: 'scheduled' })} className={`flex items-center justify-center gap-2 p-3 rounded-lg border-2 min-h-[48px] ${schedule.startMode === 'scheduled' ? 'border-[var(--color-primary)] bg-[var(--color-primary)]/5' : 'border-[var(--border-light)]'}`} data-testid="start-scheduled">
                    <Calendar className="w-4 h-4" /> {t("programming:text50")}</button>
                </div>

                {schedule.startMode === 'scheduled' && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-[var(--text-tertiary)]" />
                      <input type="time" value={schedule.startTime} onChange={(e) => setSchedule({ ...schedule, startTime: e.target.value })} className="px-3 py-2 rounded-lg border border-[var(--border-light)] min-h-[44px]" data-testid="start-time" />
                    </div>
                    {(schedule.recurrence === 'once' || schedule.recurrence === 'cyclic') && (
                      <label>{t("programming:text51")}<input type="date" aria-label={t("programming:text51")} value={schedule.startDate || ''} onChange={e => setSchedule({...schedule, startDate: e.target.value})} /></label>
                    )}
                    {/* Frequency */}
                    <div className="flex flex-wrap gap-2">
                      {(['once', 'daily', 'weekly', 'cyclic'] as RecurrenceType[]).map(r => (
                        <button key={r} onClick={() => setSchedule({ ...schedule, recurrence: r })} className={`px-3 py-2 rounded-lg text-sm min-h-[44px] ${schedule.recurrence === r ? 'bg-[var(--color-primary)] text-white' : 'bg-[var(--gray-100)] text-[var(--text-secondary)]'}`} data-testid={`recurrence-${r}`}>
                          {r === 'once' ? t("programming:text52") : r === 'daily' ? t("programming:text53") : r === 'weekly' ? t("programming:text54") : t("programming:text55")}
                        </button>
                      ))}
                    </div>
                    {schedule.recurrence === 'weekly' && (
                      <div className="flex flex-wrap gap-1.5">
                        {weekDays().map(day => {
                          const active = schedule.weekDays?.includes(day.value);
                          return (
                            <button key={day.value} onClick={() => {
                              const days = schedule.weekDays || [];
                              setSchedule({ ...schedule, weekDays: active ? days.filter(d => d !== day.value) : [...days, day.value] });
                            }} className={`w-11 h-11 rounded-lg text-xs font-medium ${active ? 'bg-[var(--color-primary)] text-white' : 'bg-[var(--gray-100)] text-[var(--text-secondary)]'}`} data-testid={`weekday-${day.value}`}>
                              {day.label}
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {schedule.recurrence === 'cyclic' && (
                      <div className="flex items-center gap-2 text-sm">
                        <span className="text-[var(--text-secondary)]">{t("programming:text56")}</span>
                        <input type="number" value={schedule.cycleIntervalDays || 3} onChange={(e) => setSchedule({ ...schedule, cycleIntervalDays: parseInt(e.target.value) || 1 })} className="w-16 px-2 py-2 rounded-lg border border-[var(--border-light)] text-center min-h-[44px]" data-testid="cycle-interval" />
                        <span className="text-[var(--text-secondary)]">{t("programming:text57")}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Summary + Timeline */}
              <div className="rounded-xl border border-[var(--border-light)] overflow-hidden">
                <div className="px-4 py-3 bg-[var(--gray-50)] border-b border-[var(--border-light)]">
                  <h3 className="font-semibold text-[var(--text-primary)]">{name || t("programming:text58")}</h3>
                  <p className="text-xs text-[var(--text-tertiary)]">
                    {t("programming:text59")} {schedule.startMode === 'now' ? t("programming:text60") : schedule.startTime} · {steps.length} {t("programming:text61")} {formatDuration(totalDuration)}
                  </p>
                </div>
                <div className="p-4 space-y-3">
                  <p className="text-xs">{t("programming:text62")}</p>
                  {/* Timeline visual */}
                  <ProgramTimeline timeline={timeline} totalDuration={totalDuration} />
                  {/* Steps summary */}
                  <div className="space-y-2 pt-2">
                    {timeline.map((tl, i) => (
                      <div key={tl.step.id} className="text-sm">
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-[var(--text-primary)]">{i + 1}. {tl.step.sectorName}</span>
                          <span className="text-[var(--text-tertiary)]">{tl.startLabel} → {tl.endLabel}</span>
                        </div>
                        {tl.step.overlap_seconds > 0 && i < timeline.length - 1 && (
                          <p className="text-xs text-[var(--color-info)] ml-3">{t("programming:text63")}{tl.step.overlap_seconds} {t("programming:text42")}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {(error || validationError) && <p role="alert" className="p-4 text-red-700">{error || validationError}</p>}
        {/* Footer navigation */}
        <div className="shrink-0 px-4 sm:px-6 py-4 border-t border-[var(--border-light)] flex items-center justify-between gap-3">
          {step > 1 ? (
            <button onClick={() => setStep(step - 1)} className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-[var(--text-secondary)] hover:bg-[var(--gray-100)] min-h-[48px]" data-testid="wizard-back">
              <ChevronLeft className="w-5 h-5" /> {t("programming:text64")}</button>
          ) : <div />}

          {step < totalSteps ? (
            <button onClick={() => setStep(step + 1)} disabled={!canProceed()} className="flex items-center gap-2 px-6 py-2.5 rounded-lg bg-[var(--color-primary)] text-white font-medium hover:bg-[var(--color-primary-700)] disabled:opacity-40 min-h-[48px]" data-testid="wizard-next">
              {t("programming:text65")}<ChevronRight className="w-5 h-5" />
            </button>
          ) : (
            <button onClick={handleSave} disabled={saving || !canProceed()} className="flex items-center gap-2 px-6 py-2.5 rounded-lg bg-[var(--color-success)] text-white font-medium hover:opacity-90 disabled:opacity-40 min-h-[48px]" data-testid="wizard-save">
              <Check className="w-5 h-5" /> {saving ? t("programming:text66") : t("programming:text67")}
            </button>
          )}
        </div>
      </div>
    </div>
    </ProgrammingDialog>
  );
};

// Timeline visualization component
const ProgramTimeline: React.FC<{ timeline: ReturnType<typeof computeTimeline>; totalDuration: number }> = ({ timeline, totalDuration }) => {
  if (timeline.length === 0 || totalDuration === 0) return null;
  const maxEnd = Math.max(...timeline.map(t => t.endMinute));
  const colors = ['#4CAF50', '#9C27B0', '#FF9800', '#2196F3', '#E91E63', '#00BCD4', '#795548'];

  return (
    <div className="space-y-1.5" data-testid="program-timeline">
      {timeline.map((tl, i) => {
        const leftPercent = (tl.startMinute / maxEnd) * 100;
        const widthPercent = ((tl.endMinute - tl.startMinute) / maxEnd) * 100;
        return (
          <div key={tl.step.id} className="relative h-6">
            <div
              className="absolute h-full rounded flex items-center px-2 overflow-hidden"
              style={{ left: `${leftPercent}%`, width: `${widthPercent}%`, backgroundColor: colors[i % colors.length], minWidth: '40px' }}
              title={`${tl.step.sectorName}: ${tl.startLabel}-${tl.endLabel}`}
            >
              <span className="text-[10px] text-white font-medium truncate">{tl.step.sectorName}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ProgramWizard;
